<script setup lang="ts">
import { ref, computed, watchEffect, onMounted } from 'vue';
import type { StepAnswer, QuestionItem, Filters, Settings, Program } from '../types/general';
import VStepAnswer from './StepAnswer.vue';
import StepSelect from './StepSelect.vue';
import StepResult from './StepResult.vue';
import StepsLayout from '../../shared-components/StepsLayout.vue';

const props = defineProps<{
  stepsPath: QuestionItem[];
  settings: Settings;
  programs: Program[];
}>();

const currentStep = ref<QuestionItem>(props.stepsPath[0]);
const pathHistory = ref<string[]>([]);
const filters = ref<Filters>({
  us_citizen: null,
  administrative: null,
  education: null,
  travel: null,
  duration: null,
  countries: null,
});

const filteredPrograms = computed(() => {
  return props.programs.filter((program: { [key: string]: any }) => {
    return Object.entries(filters.value).every(([key, value]) => {
      if (!value) return true;
      if (key === 'duration') return program.duration.type === value;
      // If selected country is in the list of countries || by filter value
      return Array.isArray(program[key]) ? program[key].includes(value) : program[key] === value;
    });
  });
});

const recomends = computed(() => {
  // return programs that have same filters.value.us_citizen and are not in filteredPrograms
  return props.programs.filter((program: { [key: string]: any }) => {
    return program.us_citizen === filters.value.us_citizen && !filteredPrograms.value.some((filteredProgram) => filteredProgram.nid === program.nid);
  });
});

watchEffect(() => {
  if (pathHistory.value.length > 0) {
    // Store to session storage
    sessionStorage.setItem('pathHistory', JSON.stringify(pathHistory.value));
    sessionStorage.setItem('currentStep', JSON.stringify(currentStep.value));
    sessionStorage.setItem('filters', JSON.stringify(filters.value));
  }
});

// restore from session storage
onMounted(() => {
  const pathHistoryStorage = sessionStorage.getItem('pathHistory');
  const currentStepStorage = sessionStorage.getItem('currentStep');
  const filtersStorage = sessionStorage.getItem('filters');

  currentStep.value = currentStepStorage ? JSON.parse(currentStepStorage) : props.stepsPath[0];
  pathHistory.value = pathHistoryStorage ? JSON.parse(pathHistoryStorage) : [];
  filters.value = filtersStorage
    ? JSON.parse(filtersStorage)
    : {
        us_citizen: null,
        administrative: null,
        education: null,
        travel: null,
        duration: null,
        countries: null,
      };
});

const nextStep = (stepName: string) => {
  // Find next step by step name
  const nextStepObj = props.stepsPath.find((step) => step.stepName === stepName);
  if (nextStepObj?.stepName) {
    pathHistory.value.push(currentStep.value.stepName);
    currentStep.value = nextStepObj;
  }
};

const moveStep = (answer: StepAnswer) => {
  // @ts-ignore
  if (answer.value) filters.value[currentStep.value.filterName] = answer.value;
  nextStep(answer.nextStepName);
};

const moveCountryStep = (answer: string) => {
  // Create country group for India and Bangladesh
  const countryGroup3 = ['IN', 'BD'];

  if (answer) {
    filters.value.countries = answer;

    // Different path for different country groups
    if (props.settings.country_group_no_educator.includes(answer)) nextStep('duration');
    else if (props.settings.country_group_with_educator.includes(answer)) nextStep('education');
    else if (countryGroup3.includes(answer)) nextStep('educationInBd');
    else nextStep('end');
  }
};

const goBack = () => {
  const previousStepName = pathHistory.value.pop();
  const previousStep = props.stepsPath.find((step) => step.stepName === previousStepName);
  if (previousStep?.stepName) {
    currentStep.value = previousStep;
    // @ts-ignore
    filters.value[currentStep.value.filterName] = null; // Reset filter value
  }
};
const startOver = () => {
  pathHistory.value = [];
  currentStep.value = props.stepsPath[0];
  filters.value = {
    us_citizen: null,
    administrative: null,
    education: null,
    travel: null,
    duration: null,
    countries: null,
  };

  sessionStorage.removeItem('pathHistory');
  sessionStorage.removeItem('currentStep');
  sessionStorage.removeItem('filters');
};

const stepsCount = computed(() => {
  return currentStep.value.stepName === 'end' ? 4 : pathHistory.value.length;
});
</script>

<template>
  <div class="program-finder">
    <StepsLayout
      title="In Progress"
      :steps-count="4"
      :current-step="stepsCount"
      :is-next="false"
      class="program-finder-steps"
      :class="{ error: !filteredPrograms.length }"
      @go-back="goBack"
      @start-over="startOver"
    >
      <template
        v-for="item in stepsPath"
        :key="item.stepName"
      >
        <VStepAnswer
          v-if="currentStep?.stepName === item.stepName && item.stepType === 'StepAnswer' && item.answers"
          :question="item.question"
          :step-answers="item.answers"
          @answer-selected="moveStep"
        />
        <StepSelect
          v-if="currentStep?.stepName === item.stepName && item.stepType === 'StepSelect'"
          :question="item.question"
          :countries="settings.countries"
          @answer-selected="moveCountryStep"
        />
      </template>
    </StepsLayout>

    <StepResult
      v-if="currentStep?.stepName === 'end' || !filteredPrograms.length"
      :results="filteredPrograms"
      :settings="settings"
      :recomends="recomends"
      @start-over="startOver"
    />
  </div>
</template>
